import React, { useRef, useCallback, useEffect, useState } from 'react';
import { LexicalEditor } from 'lexical';
import LexicalResumeEditor from './LexicalResumeEditor';
import { exportToPDF } from './utils/exportResume';
import { MarginProvider, useMargins } from './MarginContext';
import './ResumeEditorModal.css';

interface ResumeEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialContent?: string;
  onSave?: (content: string) => void;
}

interface ResumeEditorModalInnerProps extends ResumeEditorModalProps {}

const ResumeEditorModalInner: React.FC<ResumeEditorModalInnerProps> = ({
  isOpen,
  onClose,
  initialContent,
  onSave,
}) => {
  const editorRef = useRef<LexicalEditor | null>(null);
  const [content, setContent] = useState<string>(initialContent || '');
  const [isExporting, setIsExporting] = useState(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const { margins } = useMargins();

  // Update content when initialContent changes
  useEffect(() => {
    if (initialContent) {
      setContent(initialContent);
    }
  }, [initialContent]);

  const handleContentChange = useCallback((newContent: string) => {
    setContent(newContent);
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(content);
    }
    onClose();
  }, [content, onSave, onClose]);

  const handleExportPDF = useCallback(async () => {
    if (!editorContainerRef.current) return;
    setIsExporting(true);
    try {
      await exportToPDF(editorContainerRef.current, margins);
    } catch (error) {
      console.error('Failed to export PDF:', error);
    } finally {
      setIsExporting(false);
    }
  }, [margins]);

  // Reset scroll position when modal opens
  useEffect(() => {
    if (isOpen && editorContainerRef.current) {
      // Reset scroll to top when opening - use setTimeout to ensure DOM is ready
      const timer = setTimeout(() => {
        const editorInner = editorContainerRef.current?.querySelector('.lexical-editor-inner');
        if (editorInner) {
          editorInner.scrollTop = 0;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, initialContent]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, handleSave]);

  return (
    <div className="resume-editor-overlay">
      <div className="resume-editor-modal">
        {/* Header */}
        <div className="resume-editor-header">
          <div className="resume-editor-header-left">
            <div className="resume-editor-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#a3e635" strokeWidth="1.5">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
            <div className="resume-editor-title-group">
              <h2 className="resume-editor-title">Resume Editor</h2>
              <p className="resume-editor-subtitle">Edit and format your professional resume</p>
            </div>
          </div>
          <div className="resume-editor-header-right">
            <button
              type="button"
              className="resume-editor-close"
              onClick={onClose}
              title="Close (Esc)"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Editor Content */}
        <div className="resume-editor-content" ref={editorContainerRef}>
          <div className="resume-editor-main">
            <LexicalResumeEditor
              initialContent={initialContent}
              onChange={handleContentChange}
              editorRef={editorRef}
              margins={margins}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="resume-editor-footer">
          <div className="resume-editor-footer-left">
            <button
              type="button"
              className="resume-editor-btn resume-editor-btn--secondary"
              onClick={handleExportPDF}
              disabled={isExporting}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <polyline points="9 15 12 12 15 15" />
              </svg>
              Export PDF
            </button>
          </div>
          <div className="resume-editor-footer-right">
            <button
              type="button"
              className="resume-editor-btn resume-editor-btn--ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="resume-editor-btn resume-editor-btn--primary"
              onClick={handleSave}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                <polyline points="17 21 17 13 7 13 7 21" />
                <polyline points="7 3 7 8 15 8" />
              </svg>
              Save Resume
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResumeEditorModal: React.FC<ResumeEditorModalProps> = (props) => {
  if (!props.isOpen) return null;
  
  return (
    <MarginProvider>
      <ResumeEditorModalInner {...props} />
    </MarginProvider>
  );
};

export default ResumeEditorModal;
